/**
 * PDFViewerService
 * Service singleton pour gérer l'état global de la modale de visionneuse PDF.
 * Permet d'ouvrir/fermer la visionneuse depuis n'importe où dans l'application.
 */

class PDFViewerService {
    constructor() {
        this.listeners = new Set();
        this.state = {
            isOpen: false,
            source: null,
            title: '',
            subtitle: '',
            filename: '',
            onClose: null,
            interactive: false,
            disableNative: false,
            notice: null,
        };
    }

    /**
     * Ouvre la visionneuse PDF
     * @param {Object} options
     * @param {string|Uint8Array|ArrayBuffer} options.source - URL, Blob URL, ou bytes du PDF
     * @param {string} [options.title='Document PDF'] - Titre de la modale
     * @param {string} [options.subtitle=''] - Sous-titre de la modale
     * @param {string} [options.filename='document.pdf'] - Nom du fichier pour le téléchargement
     * @param {Function} [options.onClose] - Callback appelé à la fermeture
     * @param {boolean} [options.interactive=false] - Le PDF contient un formulaire interactif
     * @param {boolean} [options.disableNative=false] - Force l'utilisation de react-pdf (debug)
     * @param {object} [options.notice=null] - Bandeau d'information {message, action_label, onAction}
     */
    open({
        source,
        title = 'Document PDF',
        subtitle = '',
        filename = 'document.pdf',
        onClose = null,
        interactive = false,
        disableNative = false,
        notice = null,
    }) {
        if (!source) {
            console.error('PDFViewerService.open: source is required');
            return;
        }

        // Normaliser la source en Blob URL si c'est un ArrayBuffer ou Uint8Array
        let normalizedSource = source;
        let createdBlobUrl = null;

        if (source instanceof ArrayBuffer || source instanceof Uint8Array) {
            const blob = new Blob([source], {type: 'application/pdf'});
            createdBlobUrl = URL.createObjectURL(blob);
            normalizedSource = createdBlobUrl;
        }

        // Générer une key unique pour forcer le rechargement du Document
        const documentKey = `pdf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        this.state = {
            isOpen: true,
            source: normalizedSource,
            title,
            subtitle,
            filename,
            onClose,
            interactive,
            disableNative,
            notice,
            documentKey, // Key unique pour forcer le remontage du composant Document
            _createdBlobUrl: createdBlobUrl, // Stocké pour révocation ultérieure
        };

        this.notifyListeners();
    }

    /**
     * Ferme la visionneuse PDF
     */
    close() {
        // Révoquer le Blob URL si on l'a créé
        if (this.state._createdBlobUrl) {
            URL.revokeObjectURL(this.state._createdBlobUrl);
        }

        // Appeler le callback onClose si fourni
        if (this.state.onClose && typeof this.state.onClose === 'function') {
            this.state.onClose();
        }

        this.state = {
            isOpen: false,
            source: null,
            title: '',
            filename: '',
            onClose: null,
            interactive: false,
            disableNative: false,
            notice: null,
            documentKey: null,
            _createdBlobUrl: null,
        };

        this.notifyListeners();
    }

    /**
     * Enregistre un listener pour les changements d'état
     * @param {Function} listener - Fonction appelée lors des changements d'état
     * @returns {Function} Fonction de désinscription
     */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * Récupère l'état actuel
     * @returns {Object} État actuel
     */
    getState() {
        return {...this.state};
    }

    /**
     * Notifie tous les listeners
     */
    notifyListeners() {
        this.listeners.forEach((listener) => {
            listener(this.getState());
        });
    }
}

// Export d'une instance singleton
export default new PDFViewerService();
